/**
 * Stornobedingungen — Vorschlag, nicht Pflicht.
 *
 * WARUM SIE FREIWILLIG SIND: ein Vermieter, der nichts vereinbart, steht nicht ohne
 * Rechte da. Ohne gesonderte Abrede gelten die gesetzlichen Bestimmungen — in
 * Österreich § 1107 ABGB, in Deutschland die Regeln zum Beherbergungsvertrag. Beide
 * laufen auf dasselbe hinaus: der Gast schuldet den vereinbarten Preis abzüglich
 * ersparter Aufwendungen und dessen, was durch anderweitige Vermietung hereinkommt.
 * Eine Eingabepflicht wäre also nicht nur lästig, sie wäre sachlich unbegründet.
 *
 * WARUM ES DEN VORSCHLAG TROTZDEM GIBT: die gesetzliche Regel verlangt im Streitfall,
 * dass der Vermieter seinen Schaden rechnet und belegt. Eine vereinbarte Staffel nimmt
 * ihm genau das ab — sie ist der Grund, warum Betriebe überhaupt welche verwenden. Wer
 * eine will, soll nicht bei einem leeren Feld anfangen müssen.
 *
 * WARUM DER SCHUTZABSATZ IN JEDER FASSUNG STEHT: eine pauschalierte Entschädigung in
 * AGB gegenüber Verbrauchern ist nur wirksam, wenn dem Gast der Nachweis offen bleibt,
 * dass kein oder ein geringerer Schaden entstanden ist, und wenn ersparte Aufwendungen
 * angerechnet werden (§ 309 Nr. 5 BGB, in Österreich § 879 Abs. 3 ABGB). Eine Staffel
 * ohne diesen Absatz kippt im Zweifel vollständig — und dann steht der Vermieter
 * schlechter da als mit gar keiner Klausel, weil ihm auch die Staffel nicht mehr hilft.
 * Deshalb ist der Absatz Teil jeder Vorlage und nicht eine Option daneben.
 *
 * ANWALTLICH ZU PRÜFEN: die drei Staffeln und der Schutzabsatz. Sie gehen an jeden
 * Betrieb, der sie übernimmt — eine einzige Prüfung wirkt hier in vielen Verträgen.
 * Ausgeliefert auf ausdrückliche Entscheidung des Betreibers vom 27.07.2026.
 */

export type TermsPresetKey = 'lenient' | 'standard' | 'strict'

export interface TermsPreset {
  key: TermsPresetKey
  /** Kurzname für die Auswahl. */
  label: { de: string; en: string }
  /** Eine Zeile, die den Unterschied zu den anderen erklärt. */
  hint: { de: string; en: string }
  text: { de: string; en: string }
}

/** Der Absatz, ohne den eine Staffel gegenüber Verbrauchern angreifbar ist. */
const SAFEGUARD = {
  de:
    'Von diesen Beträgen ziehen wir ersparte Aufwendungen sowie Einnahmen ab, die wir '
    + 'durch eine anderweitige Vermietung der Einheit erzielen. Ihnen bleibt der Nachweis '
    + 'vorbehalten, dass kein oder ein wesentlich geringerer Schaden entstanden ist; in '
    + 'diesem Fall schulden Sie nur den nachgewiesenen Betrag.',
  en:
    'From these amounts we deduct expenses saved and any income from re-letting the unit. '
    + 'You remain free to show that no damage, or substantially less damage, was incurred; '
    + 'in that case you owe only the amount shown.',
}

const HEAD = {
  de:
    'Eine Absage ist jederzeit möglich. Bitte teilen Sie sie uns in Textform mit (eine '
    + 'E-Mail genügt). Maßgeblich ist der Tag, an dem Ihre Absage bei uns eingeht.',
  en:
    'You may cancel at any time. Please tell us in text form (an email is enough). What '
    + 'counts is the day your cancellation reaches us.',
}

function compose(rows: { de: string; en: string }[]): { de: string; en: string } {
  return {
    de: [HEAD.de, '', ...rows.map((r) => r.de), '', SAFEGUARD.de].join('\n'),
    en: [HEAD.en, '', ...rows.map((r) => r.en), '', SAFEGUARD.en].join('\n'),
  }
}

export const TERMS_PRESETS: TermsPreset[] = [
  {
    key: 'lenient',
    label: { de: 'Kulant', en: 'Lenient' },
    hint: {
      de: 'Lange kostenfrei. Passt, wenn kurzfristige Nachvermietung meist klappt.',
      en: 'Free for a long time. Fits when re-letting at short notice usually works.',
    },
    text: compose([
      { de: 'bis 14 Tage vor Anreise: kostenfrei', en: 'up to 14 days before arrival: free of charge' },
      { de: '13 bis 3 Tage vor Anreise: 50 % des Gesamtpreises', en: '13 to 3 days before arrival: 50 % of the total price' },
      { de: 'ab 2 Tagen vor Anreise und bei Nichtanreise: 80 % des Gesamtpreises', en: 'from 2 days before arrival and for no-shows: 80 % of the total price' },
    ]),
  },
  {
    key: 'standard',
    label: { de: 'Üblich', en: 'Standard' },
    hint: {
      de: 'Die Staffel, die die meisten Betriebe verwenden.',
      en: 'The ladder most properties use.',
    },
    text: compose([
      { de: 'bis 30 Tage vor Anreise: kostenfrei', en: 'up to 30 days before arrival: free of charge' },
      { de: '29 bis 15 Tage vor Anreise: 50 % des Gesamtpreises', en: '29 to 15 days before arrival: 50 % of the total price' },
      { de: '14 bis 7 Tage vor Anreise: 70 % des Gesamtpreises', en: '14 to 7 days before arrival: 70 % of the total price' },
      { de: 'ab 6 Tagen vor Anreise und bei Nichtanreise: 90 % des Gesamtpreises', en: 'from 6 days before arrival and for no-shows: 90 % of the total price' },
    ]),
  },
  {
    key: 'strict',
    label: { de: 'Streng', en: 'Strict' },
    hint: {
      de: 'Für gefragte Zeiten und lange Mindestaufenthalte.',
      en: 'For sought-after periods and long minimum stays.',
    },
    text: compose([
      { de: 'bis 60 Tage vor Anreise: kostenfrei', en: 'up to 60 days before arrival: free of charge' },
      { de: '59 bis 30 Tage vor Anreise: 50 % des Gesamtpreises', en: '59 to 30 days before arrival: 50 % of the total price' },
      { de: '29 bis 14 Tage vor Anreise: 80 % des Gesamtpreises', en: '29 to 14 days before arrival: 80 % of the total price' },
      { de: 'ab 13 Tagen vor Anreise und bei Nichtanreise: 90 % des Gesamtpreises', en: 'from 13 days before arrival and for no-shows: 90 % of the total price' },
    ]),
  },
]

/**
 * Was der Gast liest, wenn der Betrieb nichts vereinbart hat.
 *
 * WARUM NICHT DER ALTE WARNHINWEIS: dort stand „Bitte klären Sie vor der Buchung mit
 * Ihrem Gastgeber, was bei einer Absage gilt." Das ist eine Warnung an der Stelle, an
 * der der Gast gerade buchen will, und sie ist sachlich falsch — es ist nichts
 * ungeklärt, es gilt das Gesetz. Der Satz hat Buchungen gekostet und dem Vermieter
 * eine Rückfrage eingebracht, auf die er keine bessere Antwort hat als „das Gesetz".
 *
 * WARUM ER KEIN LAND NENNT: der Betrieb kann in Österreich sitzen und der Gast in
 * Deutschland wohnen. „Die gesetzlichen Bestimmungen" stimmt in beiden Fällen; eine
 * Paragrafenangabe wäre in der Hälfte der Fälle die falsche.
 */
export const STATUTORY_FALLBACK = {
  de:
    'Es wurden keine gesonderten Stornobedingungen vereinbart. Bei einer Absage gelten '
    + 'die gesetzlichen Bestimmungen.',
  en:
    'No separate cancellation terms have been agreed. If you cancel, the statutory '
    + 'provisions apply.',
}
